"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useConfirm } from "./ConfirmDialog";

interface Entry {
  name: string;
  dir: boolean;
}

interface OpenFile {
  path: string;
  text: string;
  size: number;
  mtimeMs: number;
}

/** 1 フォルダで 1 度に並べる件数 */
const PAGE = 300;

const join = (parent: string, name: string) => (parent === "" ? name : `${parent}/${name}`);

/** 世界の master フォルダを閲覧・編集する画面。PC 用 */
export default function MasterFileEditor({ world, onBack }: { world: string; onBack: () => void }) {
  // 読んだフォルダの中身。キーは master からの相対パス
  const [dirs, setDirs] = useState<Record<string, Entry[]>>({});
  const [opened, setOpened] = useState<Set<string>>(new Set([""]));
  const [loading, setLoading] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [limits, setLimits] = useState<Record<string, number>>({});

  const [file, setFile] = useState<OpenFile | null>(null);
  const [draft, setDraft] = useState("");
  const [status, setStatus] = useState("");
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ask, confirmDialog] = useConfirm();

  const dirty = file !== null && draft !== file.text;
  const dirtyRef = useRef(false);
  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  const say = (text: string, isError = false) => {
    setStatus(text);
    setFailed(isError);
  };

  const loadDir = useCallback(
    async (path: string) => {
      setLoading((prev) => new Set(prev).add(path));
      try {
        const res = await fetch(
          `/api/admin/master/list?world=${encodeURIComponent(world)}&path=${encodeURIComponent(path)}`
        );
        const data = await res.json();
        if (!res.ok) {
          say(data.error ?? "フォルダを読めない", true);
          return;
        }
        setDirs((prev) => ({ ...prev, [path]: data.entries as Entry[] }));
      } catch {
        say("フォルダを読めない", true);
      } finally {
        setLoading((prev) => {
          const next = new Set(prev);
          next.delete(path);
          return next;
        });
      }
    },
    [world]
  );

  useEffect(() => {
    loadDir("");
  }, [loadDir]);

  // 保存前の変更を持ったままページを離れる場合に確かめる
  useEffect(() => {
    const guard = (e: BeforeUnloadEvent) => {
      if (dirtyRef.current) e.preventDefault();
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, []);

  const mayDiscard = async () =>
    !dirtyRef.current || (await ask("保存前の変更があります。捨てて進みますか？", { ok: "捨てて進む", danger: true }));

  const toggleDir = (path: string) => {
    setOpened((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
    if (!dirs[path]) loadDir(path);
  };

  const openFile = async (path: string) => {
    if (busy || !(await mayDiscard())) return;
    setBusy(true);
    say("読み込み中・・・");
    try {
      const res = await fetch(
        `/api/admin/master/file?world=${encodeURIComponent(world)}&path=${encodeURIComponent(path)}`
      );
      const data = await res.json();
      if (!res.ok) {
        say(`${path}：${data.error ?? "読めない"}`, true);
        return;
      }
      setFile(data as OpenFile);
      setDraft(data.text);
      say("");
    } catch {
      say("ファイルを読めない", true);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!file || !dirty || busy) return;
    setBusy(true);
    say("保存しています");
    try {
      const res = await fetch("/api/admin/master/file", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ world, path: file.path, text: draft, mtimeMs: file.mtimeMs }),
      });
      const data = await res.json();
      if (!res.ok) {
        say(data.error ?? "保存できない", true);
        return;
      }
      setFile(data as OpenFile);
      setDraft(data.text);
      say("保存しました");
    } catch {
      say("保存できない", true);
    } finally {
      setBusy(false);
    }
  };

  const saveRef = useRef(save);
  useEffect(() => {
    saveRef.current = save;
  });

  // Ctrl+S で保存する
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        saveRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const rowStyle = (depth: number, on: boolean): React.CSSProperties => ({
    display: "block",
    width: "100%",
    padding: `3px 8px 3px ${8 + depth * 14}px`,
    fontSize: 13,
    fontFamily: "inherit",
    textAlign: "left",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    background: on ? "rgba(200,162,74,0.14)" : "transparent",
    color: on ? "var(--accent-strong)" : "var(--text)",
    border: "none",
    cursor: "pointer",
  });
  const smallButton = (primary: boolean): React.CSSProperties => ({
    padding: "5px 14px",
    fontSize: 13,
    fontFamily: "inherit",
    background: primary ? "var(--accent)" : "var(--bg-field)",
    color: primary ? "#1a1509" : "var(--text)",
    fontWeight: primary ? 700 : 400,
    border: `1px solid ${primary ? "var(--accent)" : "var(--line)"}`,
    borderRadius: 6,
    cursor: "pointer",
  });

  /** フォルダの中身を行に並べる */
  const renderDir = (path: string, depth: number): React.ReactNode[] => {
    const entries = dirs[path];
    if (!entries) {
      return [
        <div key={`${path}//wait`} style={{ ...rowStyle(depth, false), color: "var(--text-dim)", cursor: "default" }}>
          {loading.has(path) ? "読み込み中・・・" : ""}
        </div>,
      ];
    }

    const rows: React.ReactNode[] = [];
    const filter = (filters[path] ?? "").toLowerCase();
    const matched = filter === "" ? entries : entries.filter((x) => x.name.toLowerCase().includes(filter));
    const limit = limits[path] ?? PAGE;

    if (entries.length > PAGE) {
      rows.push(
        <div key={`${path}//filter`} style={{ padding: `3px 8px 3px ${8 + depth * 14}px` }}>
          <input
            value={filters[path] ?? ""}
            onChange={(e) => setFilters((prev) => ({ ...prev, [path]: e.target.value }))}
            placeholder={`絞り込み（全 ${entries.length} 件）`}
            style={{
              width: "100%",
              fontSize: 12,
              fontFamily: "inherit",
              padding: "3px 6px",
              color: "var(--text)",
              background: "var(--bg-field)",
              border: "1px solid var(--line)",
              borderRadius: 4,
            }}
          />
        </div>
      );
    }

    for (const entry of matched.slice(0, limit)) {
      const child = join(path, entry.name);
      if (entry.dir) {
        const isOpen = opened.has(child);
        rows.push(
          <button key={child} style={rowStyle(depth, false)} onClick={() => toggleDir(child)} title={child}>
            {isOpen ? "▾" : "▸"} {entry.name}
          </button>
        );
        if (isOpen) rows.push(...renderDir(child, depth + 1));
      } else {
        rows.push(
          <button key={child} style={rowStyle(depth, file?.path === child)} onClick={() => openFile(child)} title={child}>
            {entry.name}
          </button>
        );
      }
    }

    if (matched.length > limit) {
      rows.push(
        <button
          key={`${path}//more`}
          style={{ ...rowStyle(depth, false), color: "var(--accent)" }}
          onClick={() => setLimits((prev) => ({ ...prev, [path]: limit + PAGE }))}
        >
          さらに表示（残り {matched.length - limit} 件）
        </button>
      );
    }
    return rows;
  };

  return (
    <div style={{ position: "fixed", inset: 0, display: "flex", background: "var(--bg)", color: "var(--text)" }}>
      {confirmDialog}
      <div
        style={{
          width: 340,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          borderRight: "1px solid var(--line)",
          background: "var(--bg-panel)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 12px",
            borderBottom: "1px solid var(--line)",
          }}
        >
          <strong style={{ color: "var(--accent)", letterSpacing: "0.1em" }}>{world} master</strong>
          <button
            style={smallButton(false)}
            onClick={async () => {
              if (await mayDiscard()) onBack();
            }}
          >
            戻る
          </button>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "6px 0" }}>{renderDir("", 0)}</div>
      </div>

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "10px 14px",
            borderBottom: "1px solid var(--line)",
          }}
        >
          <div style={{ flex: 1, minWidth: 0, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {file ? (
              <>
                {dirty ? "● " : ""}
                {file.path}
                <span style={{ color: "var(--text-dim)" }}>　{file.size.toLocaleString()} バイト</span>
              </>
            ) : (
              <span style={{ color: "var(--text-dim)" }}>左の一覧からファイルを選んでください</span>
            )}
          </div>
          <span style={{ fontSize: 12, color: failed ? "var(--danger)" : "var(--text-dim)" }}>{status}</span>
          <button
            style={{ ...smallButton(false), opacity: dirty && !busy ? 1 : 0.5 }}
            disabled={!dirty || busy}
            onClick={async () => {
              if (file && (await ask("変更を捨てて、開いたときの本文へ戻しますか？", { ok: "元に戻す", danger: true }))) setDraft(file.text);
            }}
          >
            元に戻す
          </button>
          <button style={{ ...smallButton(true), opacity: dirty && !busy ? 1 : 0.5 }} disabled={!dirty || busy} onClick={save}>
            保存
          </button>
        </div>

        {file ? (
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck={false}
            style={{
              flex: 1,
              width: "100%",
              resize: "none",
              border: "none",
              outline: "none",
              padding: "14px 16px",
              fontSize: 14,
              lineHeight: 1.6,
              fontFamily: '"BIZ UDGothic", "MS Gothic", Consolas, monospace',
              color: "var(--text)",
              background: "var(--bg)",
              tabSize: 2,
            }}
          />
        ) : (
          <div style={{ flex: 1 }} />
        )}
      </div>
    </div>
  );
}
